import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import TaskForm from '../components/TaskForm';
import TaskTable from '../components/TaskTable';
import { MemberDashboardSkeleton } from '../components/DashboardSkeleton';
import { useToast } from '../context/ToastContext';
import { getTasks, getUsers, addTask, updateTask, getTasksByUserName } from '../data/supabaseApi';
import { FiPlus } from 'react-icons/fi';
import './MemberDashboard.css';

const MemberDashboard = () => {
  const { addToast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [loading, setLoading] = useState(true);

  const loadTasks = async (userName = null) => {
    try {
      if (userName) {
        const list = await getTasksByUserName(userName);
        setTasks(list);
      } else {
        const all = await getTasks();
        setTasks(all);
      }
    } catch (e) {
      console.error(e);
      setTasks([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function init() {
      const usersList = await getUsers();
      if (cancelled) return;
      setUsers(usersList);
      setSelectedUserName('');
      await loadTasks();
      if (!cancelled) setLoading(false);
    }
    init();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleUserChange = (e) => {
      const newUserName = e.detail;
      setSelectedUserName(newUserName ?? '');
      loadTasks(newUserName || null);
    };
    window.addEventListener('userNameChanged', handleUserChange);
    window.addEventListener('storage', (e) => {
      if (e.key === 'selectedUserName') {
        const u = localStorage.getItem('selectedUserName');
        setSelectedUserName(u ?? '');
        loadTasks(u || null);
      }
    });
    return () => window.removeEventListener('userNameChanged', handleUserChange);
  }, []);

  const handleAddTask = async (taskData) => {
    try {
      const usersList = await getUsers();
      const user = usersList.find(u => u.name === taskData.assignedMember);
      await addTask({ ...taskData, userId: user?.id || 0 });
      await loadTasks(selectedUserName || null);
      setShowForm(false);
      addToast('Task added successfully.', 'success');
    } catch (e) {
      console.error(e);
      addToast('Failed to add task. Please try again.', 'error');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const handleUpdateTask = async (taskData) => {
    try {
      await updateTask(editingTask.id, taskData);
      await loadTasks(selectedUserName || null);
      setEditingTask(null);
      setShowForm(false);
      addToast('Task updated successfully.', 'success');
    } catch (e) {
      console.error(e);
      addToast('Failed to update task. Please try again.', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingTask(null);
    setShowForm(false);
  };

  const currentUser = users.find(u => u.name === selectedUserName) || { name: selectedUserName || 'All Tasks', role: 'member' };

  if (loading) {
    return <MemberDashboardSkeleton />;
  }

  return (
    <div className="dashboard-layout">
      <Sidebar userRole="member" />
      <div className="dashboard-content">
        <Topbar
          userName={selectedUserName}
          userRole="member"
          onUserChange={(name) => { setSelectedUserName(name || ''); loadTasks(name || null); }}
        />
        <div className="dashboard-main">
          <div className="dashboard-header">
            <h2>My Tasks</h2>
            {!showForm && (
              <button type="button" className="add-task-btn" onClick={() => setShowForm(true)}>
                <FiPlus className="add-icon" /><span>Add Task</span>
              </button>
            )}
          </div>

          {showForm && (
            <div className="task-form-section">
              <div className="form-header">
                <h3>{editingTask ? 'Edit Task' : 'Add New Task'}</h3>
                {editingTask && <button type="button" className="cancel-btn" onClick={handleCancelEdit}>Cancel</button>}
              </div>
              <TaskForm
                onSubmit={editingTask ? handleUpdateTask : handleAddTask}
                initialData={editingTask}
              />
            </div>
          )}

          <div className="tasks-section">
            <h3>{selectedUserName ? `${selectedUserName}'s Tasks` : 'All Tasks'} ({tasks.length})</h3>
            <TaskTable tasks={tasks} onEdit={handleEditTask} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
